import { useSetupReviewContext } from '../../../accountSetup/SetupReviewContext';
import { recipeWriteVersion, StaleRecipeWriteError } from '../../../../../services/recipeWriteConflict';
import type { SourceRestaurantData } from "../../../../../stores/appSourceRestaurantDataStore";
import { Plus, SlidersHorizontal, X } from "lucide-react";
import { ensureNameOnlyCustomIngredient } from "../../../../../services/costPrice/automationRunService";
import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { usePracticeDataset, getPracticeDataset } from '../../../../onboarding/practice/practiceDataset';
import { scopedAnchor, usePracticeScope } from "../../../../onboarding/practice/practiceScope";
import {
    primaryText,
    secondaryText,
    greyTextColor,
    borderPrimary,
    placeholderPrimary,
    greenColorClass,
    cancelColorClass,
    overlayTopLayer,
} from "../../../../../styling/stylingHelper";
import { Unit, useOrderHistory } from "../../../../../stores/appOrderHistoryStore";
import { useRevenueHistory } from "../../../../../stores/appRevenueHistoryStore";
import { useAppUserDefaultStore } from "../../../../../stores/appUserDefaultStore";
import { useCostPriceStore } from "../../../../../stores/appCostPriceStore";
import { useRestaurantStore } from "../../../../../stores/appRestaurantStore";
import { useSourceRestaurantDataStore } from "../../../../../stores/appSourceRestaurantDataStore";
import { CostPriceIngredient, updateCostPriceAnalysis, fetchCostPriceAnalysisById, fetchCostPriceAnalysesByRestaurant, toIngredientUpdatePayload } from "../../../../../services/costPrice/costPriceService";
import { CustomIngredient, fetchCustomIngredientsByRestaurant } from "../../../../../services/customIngredients/customIngredientsService";
import { Subrecipe, fetchSubrecipesByRestaurant } from "../../../../../services/subrecipes/subrecipesService";
import { calculateUnitPrice } from "../../../../orderhistory/aggregations";
import { eur } from "../../../../orderhistory/utils";
import { hasSubrecipePrices } from "../../../../../utils/costPriceAvailability";
import { calculateSubrecipeTotalCost, enrichCostPriceListWithCurrentPrices } from "../../../../../utils/costPriceUtils";
import { formatQuantityInfo } from "../../../../../utils/quantityUtils";
import { applyWaste, resolveWaste } from "../../../../../utils/wasteUtils";
import { useFuzzySearch } from "../../../../../hooks/useFuzzySearch";
import { useProductSearch } from "../../../../../hooks/useProductSearch";
import { useDebounce } from "../../../../../hooks/useDebounce";
import CustomIngredientDetailModal from "./CustomIngredientDetailModal";
import { Platform } from "../../../../../types/Platforms";
import { platformsWithoutArticleId } from "../../../../../utils/platformUtils";
import { productKey } from "../../../../../utils/reports/shared";
import FromOrdersTab from "./tabs/FromOrdersTab";
import { SupplierLogo } from "../../../../ui/SupplierLogo";
import CustomIngredientsTab from "./tabs/CustomIngredientsTab";
import SubrecipesTab from "./tabs/SubrecipesTab";
import MenuItemsTab from "./tabs/MenuItemsTab";

// Lazy-loaded to break the AddIngredients ↔ SubrecipeDetailModal import cycle
// (the modal renders this picker in subrecipe mode).
const SubrecipeDetailModal = lazy(() => import("./SubrecipeDetailModal"));

/**
 * Normalized description of what the user picked in the ingredient picker.
 * The persistence layer differs by domain (cost price posts to an analysis;
 * subrecipes append to a local draft), so the picker hands the raw selection to
 * the caller's `onAddIngredient` and lets it decide how to store it.
 */
export type AddIngredientSelection =
    | { kind: "product"; product: any; quantityNeeded: number; wastePercentage?: number }
    | { kind: "custom"; customIngredient: CustomIngredient; quantityNeeded: number }
    | { kind: "subrecipe"; subrecipe: Subrecipe; quantityNeeded: number }
    | { kind: "costPriceItem"; costPriceItem: any; quantityNeeded: number };

interface AddIngredientsProps {
    onBack: () => void;
    onClose: () => void;
    // Cost price mode persists each add straight to this analysis; omitted (and
    // unused) in subrecipe mode, which persists via `onAddIngredient`.
    analysisId?: string;
    restaurantId: string;
    onIngredientAdded?: () => void;
    defaultWastePercentage?: number;
    // 'costPrice' (default) keeps every tab incl. Menu Items and the built-in
    // POST-to-analysis persistence. 'subrecipe' drops the cost-price-only Menu
    // Items tab and requires `onAddIngredient`. 'alternatives' restricts to the
    // From Orders tab only (an alternative is always a real order-history product)
    // and requires `onAddIngredient`. Automation review uses the first three tabs
    // and delegates persistence via `onAddIngredient`.
    mode?: "costPrice" | "subrecipe" | "alternatives" | "automationReview";
    inline?: boolean;
    dataOverride?: SourceRestaurantData;
    // Pluggable persistence. When provided, the picker calls this with the raw
    // selection instead of writing to `analysisId` itself. Cost price leaves it
    // undefined to keep its existing behavior.
    onAddIngredient?: (selection: AddIngredientSelection) => Promise<void> | void;
    // Subrecipe ids to hide from the Sub recipes tab — the subrecipe being edited
    // plus its ancestors, so nesting can't create a cycle (service still guards).
    excludeSubrecipeIds?: Set<string>;
    // Order-history product resolver keys (`productKey(...)`) to hide from the
    // From Orders tab — used by the Alternatives picker to exclude the primary
    // product and already-added alternatives.
    excludeProductKeys?: Set<string>;
    // Optional translated overrides for the header title + add button (the
    // Alternatives picker reads "Add alternative"). Default to the built-ins.
    title?: string;
    addLabel?: string;
    alternativePrimary?: {
        productName?: string;
        quantityNeeded?: number;
        quantityInfo?: { stdUnit: string };
        wastePercentage?: number;
    };
    // Mount with the candidate ready; remount for a different proposal.
    initialProduct?: any;
    lockProductSelection?: boolean;
    returnAfterAdd?: boolean;
}

type IngredientTab = "From Orders" | "Custom Ingredients" | "Sub recipes" | "Menu Items";

const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

// Cap how many rows we hand to the lists. The From Orders set can reach many
// thousands once sibling restaurants are added; rendering them all at once is
// what froze the picker. The cap protects the broad/empty-query view — once the
// user types, results narrow well under it.
const MAX_VISIBLE_RESULTS = 200;

/**
 * Dedup key for an order-history product line. Platforms that ship no articleId
 * key on the product name instead; the source restaurant keeps the same product
 * distinct across restaurants.
 */
const productDedupKey = (product: any): string => {
    const platformLower = product.platform?.toLowerCase() || '';
    const useNameAsKey = platformsWithoutArticleId.includes(platformLower);
    const sourceKey = product.sourceRestaurantId ?? 'self';
    return useNameAsKey
        ? `${product.name}-${product.platform}-${sourceKey}`
        : `${product.articleId}-${product.platform}-${sourceKey}`;
};

const INGREDIENT_TABS: IngredientTab[] = [
    "From Orders",
    "Custom Ingredients",
    "Sub recipes",
    "Menu Items",
];

const AddIngredients: React.FC<AddIngredientsProps> = ({
    onBack,
    onClose,
    analysisId,
    restaurantId,
    onIngredientAdded,
    defaultWastePercentage = 0,
    mode = "costPrice",
    inline = false,
    dataOverride,
    onAddIngredient,
    excludeSubrecipeIds,
    excludeProductKeys,
    title,
    addLabel,
    alternativePrimary,
    initialProduct,
    lockProductSelection = false,
    returnAfterAdd = true,
}) => {
    const { t } = useTranslation();
    const practiceScope = usePracticeScope();
    const restrictedSetupSources = !!useSetupReviewContext()?.restrictedToRestaurant;
    // Menu Items (nested cost prices) is cost-price-only; subrecipes can't nest them.
    // Alternatives are always real order-history products → From Orders only.
    const availableTabs = useMemo<IngredientTab[]>(
        () => (mode === "alternatives"
            ? ["From Orders"] as IngredientTab[]
            : mode === "automationReview"
                ? ["From Orders", "Sub recipes", "Custom Ingredients"] as IngredientTab[]
            : mode === "subrecipe"
                ? INGREDIENT_TABS.filter(tab => tab !== "Menu Items")
                : INGREDIENT_TABS).filter(tab => !practiceScope || tab !== "Custom Ingredients"),
        [mode, practiceScope]
    );
    const [searchQuery, setSearchQuery] = useState<string>("");
    // Debounce the query feeding the fuzzy search so typing stays responsive even
    // when sibling-restaurant data balloons the product set into the thousands.
    const debouncedSearchQuery = useDebounce(searchQuery, 200);
    const [activeTab, setActiveTab] = useState<IngredientTab>("From Orders");
    const isAlternative = mode === "alternatives";
    const parentWaste = resolveWaste(alternativePrimary?.wastePercentage, defaultWastePercentage);
    const defaultProductQuantity = (product: any) => {
        const unit = product?.quantityInfo?.stdUnit;
        const parentUnit = alternativePrimary?.quantityInfo?.stdUnit;
        const compatible = !!unit && !!parentUnit && (unit === parentUnit ||
            (["g", "ml"].includes(unit) && ["g", "ml"].includes(parentUnit)));
        if (isAlternative && compatible && alternativePrimary?.quantityNeeded !== undefined) {
            return alternativePrimary.quantityNeeded;
        }
        return unit === Unit.GRAM || unit === Unit.MILLILITER ? 100 : 1;
    };
    const [selectedProduct, setSelectedProduct] = useState<any>(initialProduct ?? null);
    const [alternativeWaste, setAlternativeWaste] = useState<string>(String(parentWaste));
    const [selectedCustomIngredient, setSelectedCustomIngredient] = useState<CustomIngredient | null>(null);
    const [selectedSubrecipe, setSelectedSubrecipe] = useState<Subrecipe | null>(null);
    const [selectedCostPriceItem, setSelectedCostPriceItem] = useState<any>(null);
    const [quantityNeeded, setQuantityNeeded] = useState<number>(() => initialProduct ? defaultProductQuantity(initialProduct) : 1);
    const [quantityInputValue, setQuantityInputValue] = useState<string>(() => String(initialProduct ? defaultProductQuantity(initialProduct) : 1));
    const validAlternativeWaste = alternativeWaste.trim() !== "" && Number.isFinite(Number(alternativeWaste)) && Number(alternativeWaste) >= 0 && Number(alternativeWaste) <= 100;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reviewSearchOpen, setReviewSearchOpen] = useState(false);
    const [reviewFiltersOpen, setReviewFiltersOpen] = useState(false);
    const [newCustomName, setNewCustomName] = useState<string | null>(null);
    const [customUnit, setCustomUnit] = useState<"g" | "ml" | "p">("g");
    const isReview = mode === "automationReview";
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCreateSubrecipeModal, setShowCreateSubrecipeModal] = useState(false);

    const practiceData = usePracticeDataset(practiceScope);

    // Zustand stores
    const load = useOrderHistory(s => s.load);
    const liveOrders = useOrderHistory(s => s.orderHistoryList);
    const orderRestaurantId = useOrderHistory(s => s.currentRestaurantId);
    const ordersLoading = useOrderHistory(s => s.isLoading);
    const orders = practiceData?.orderHistoryList ?? dataOverride?.orders ?? liveOrders;
    const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
    // For BYO-aware nested cost price enrichment in the picker.
    const revenueHistoryList = useRevenueHistory(s => s.revenueHistoryList);

    // Cost price store - for filters
    const liveCostPriceActiveTab = useCostPriceStore(s => s.activeTab);
    const liveSupplierFilters = useCostPriceStore(s => s.addIngredientSupplierFilters);
    const setLiveSupplierFilters = useCostPriceStore(s => s.setAddIngredientSupplierFilters);
    const liveCategoryFilters = useCostPriceStore(s => s.addIngredientCategoryFilters);
    const setLiveCategoryFilters = useCostPriceStore(s => s.setAddIngredientCategoryFilters);
    const [practiceSupplierFilters, setPracticeSupplierFilters] = useState<string[]>([]);
    const [practiceCategoryFilters, setPracticeCategoryFilters] = useState<string[]>([]);
    const costPriceActiveTab = practiceScope ? 'all' : liveCostPriceActiveTab;
    const supplierFilters = practiceScope ? practiceSupplierFilters : liveSupplierFilters;
    const setSupplierFilters = practiceScope ? setPracticeSupplierFilters : setLiveSupplierFilters;
    const categoryFilters = practiceScope ? practiceCategoryFilters : liveCategoryFilters;
    const setCategoryFilters = practiceScope ? setPracticeCategoryFilters : setLiveCategoryFilters;

    // Cross-restaurant sourcing: shared selected source restaurants + sibling data cache.
    const restaurants = useRestaurantStore(s => s.restaurants);
    const liveSourceRestaurantIds = useCostPriceStore(s => s.addIngredientSourceRestaurantIds);
    const sourceRestaurantIds = useMemo(() => practiceData || dataOverride || restrictedSetupSources ? [restaurantId] : liveSourceRestaurantIds, [practiceData, dataOverride, restrictedSetupSources, restaurantId, liveSourceRestaurantIds]);
    const setSourceRestaurantIds = useCostPriceStore(s => s.setAddIngredientSourceRestaurantIds);
    const sourceDataByRestaurant = useSourceRestaurantDataStore(s => s.dataByRestaurant);
    const ensureSourceLoaded = useSourceRestaurantDataStore(s => s.ensureLoaded);

    const [liveCustomIngredients, setCustomIngredients] = useState<CustomIngredient[]>([]);
    const customIngredients = practiceData?.customIngredients ?? dataOverride?.customIngredients ?? liveCustomIngredients;
    const [liveSubrecipes, setSubrecipes] = useState<Subrecipe[]>([]);
    const subrecipes = useMemo(() => practiceData?.subrecipesList ?? (dataOverride ? Array.from(dataOverride.subrecipes.values()) : liveSubrecipes), [practiceData, dataOverride, liveSubrecipes]);
    const [liveRawCostPriceItems, setRawCostPriceItems] = useState<any[]>([]);
    const rawCostPriceItems = useMemo(() => practiceData ? [] : liveRawCostPriceItems, [practiceData, liveRawCostPriceItems]);

    // Seed the shared source-restaurant selection to the current restaurant each
    // time the picker opens (default = current restaurant only).
    useEffect(() => {
        if (restaurantId && !practiceScope && !dataOverride) setSourceRestaurantIds([restaurantId]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId]);

    // Lazily load each selected sibling restaurant's data (orders + custom + subrecipes).
    useEffect(() => {
        sourceRestaurantIds.forEach(id => {
            if (id !== restaurantId) ensureSourceLoaded(id);
        });
    }, [sourceRestaurantIds, restaurantId, ensureSourceLoaded]);

    // Resolve a sibling restaurant's display name.
    const restaurantNameById = useMemo(() => {
        const map = new Map<string, string>();
        restaurants.forEach(r => map.set(r.id, r.name));
        return map;
    }, [restaurants]);

    // Load all data on mount
    useEffect(() => {
        if (practiceScope || dataOverride || !restaurantId) return;

        const loadData = async () => {
            try {
                // Load order history if not already loaded
                if (orders.length === 0) {
                    load(restaurantId);
                }

                // Load custom ingredients
                const ingredients = await fetchCustomIngredientsByRestaurant(restaurantId);
                setCustomIngredients(ingredients);

                // Load subrecipes
                const subrecipeList = await fetchSubrecipesByRestaurant(restaurantId);
                setSubrecipes(subrecipeList);

                // Load cost price items (raw, without enrichment) — Menu Items tab
                // only exists in cost-price mode, so skip the fetch otherwise.
                if (mode === "costPrice") {
                    const costPriceList = await fetchCostPriceAnalysesByRestaurant(restaurantId);
                    const filtered = costPriceList.filter(item => item.id !== analysisId);
                    setRawCostPriceItems(filtered);
                }
            } catch (error) {
                console.error('Failed to load data:', error);
            }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, analysisId]);

    // Enrich cost price items when orders or custom ingredients change.
    // Passing revenueHistory so BYO dishes nested as ingredients carry their
    // weighted cost (otherwise they'd appear at base-only cost in the picker).
    const availableCostPriceItems = useMemo(() => {
        if (rawCostPriceItems.length === 0) return [];
        return enrichCostPriceListWithCurrentPrices(rawCostPriceItems, orders, { customIngredients, showExVat: showExVatOrders, revenueHistory: revenueHistoryList });
    }, [rawCostPriceItems, orders, customIngredients, showExVatOrders, revenueHistoryList]);

    // Get unique products from order history, unioning the current restaurant
    // (when selected) with every selected sibling restaurant. Each product is
    // tagged with its source restaurant so it can be badged, deduped distinctly,
    // and persisted with `sourceRestaurantId` on add.
    const allProducts = useMemo(() => {
        const result: any[] = [];
        const pushOrders = (
            orderList: typeof orders,
            sourceRestaurantId: string | undefined,
            sourceRestaurantName: string | undefined,
        ) => {
            orderList.forEach(order => {
                order.productData.forEach(product => {
                    result.push({
                        ...product,
                        platform: order.platform,
                        orderDate: order.orderDate,
                        quantityInfo: product.quantityInfo,
                        sourceRestaurantId,
                        sourceRestaurantName,
                    });
                });
            });
        };

        // Current restaurant (no source tag) — only when ticked.
        if (sourceRestaurantIds.includes(restaurantId)) {
            pushOrders(orders, undefined, undefined);
        }
        // Sibling restaurants.
        sourceRestaurantIds
            .filter(id => id !== restaurantId)
            .forEach(id => {
                const data = sourceDataByRestaurant[id];
                if (!data) return;
                pushOrders(data.orders as any, id, restaurantNameById.get(id));
            });
        return result;
    }, [orders, sourceRestaurantIds, restaurantId, sourceDataByRestaurant, restaurantNameById]);

    // Quantity ordered per product over the last 60 days, in std units (kg/L/pcs) —
    // the same signal the inventory product picker shows. Surfaced on each card so the
    // user can tell which of several similar products the kitchen actually buys.
    // Mirrors `buildLiveProductMap` (server/database/inventoryPricingHelper.ts):
    // qty x stdAmount x stdMultiplier, summed over every line in the window.
    const orderedLast60ByKey = useMemo(() => {
        const cutoff = Date.now() - SIXTY_DAYS_MS;
        const totals = new Map<string, { base: number; stdUnit: Unit; category?: string }>();
        allProducts.forEach(product => {
            if (new Date(product.orderDate).getTime() < cutoff) return;
            const quantityInfo = product.quantityInfo;
            const perUnit = (quantityInfo?.stdAmount ?? 0) * (quantityInfo?.stdMultiplier ?? 0);
            const orderedCount = Number(product.quantity) || 0;
            const base = Math.abs(perUnit > 0 ? orderedCount * perUnit : orderedCount);
            const stdUnit: Unit = perUnit > 0 ? (quantityInfo.stdUnit as Unit) : Unit.PIECE;
            const key = productDedupKey(product);
            const existing = totals.get(key);
            if (existing) existing.base += base;
            else totals.set(key, { base, stdUnit, category: product.category });
        });

        const display = new Map<string, string>();
        totals.forEach((total, key) => {
            display.set(key, formatQuantityInfo(
                1,
                { stdAmount: total.base, stdMultiplier: 1, stdUnit: total.stdUnit, weightString: '' },
                total.category
            ).displayText);
        });
        return display;
    }, [allProducts]);

    // Group products by articleId + platform and get the most recent one
    // For platforms without articleId, use product name instead to avoid collapsing products
    // Dedup key includes sourceRestaurantId so the same product across restaurants stays distinct.
    const uniqueProducts = useMemo<any[]>(() => Array.from(
        allProducts
            .reduce((map, product) => {
                const platformLower = product.platform?.toLowerCase() || '';
                const useNameAsKey = platformsWithoutArticleId.includes(platformLower);
                const key = productDedupKey(product);
                const existing = map.get(key);
                if (!existing || new Date(product.orderDate) > new Date(existing.orderDate)) {
                    // For platforms without articleId, use the product name as a searchable identifier
                    // This ensures fuzzy search works correctly by matching on name
                    map.set(key, {
                        ...product,
                        searchableId: useNameAsKey ? product.name : product.articleId,
                        orderedLast60: orderedLast60ByKey.get(key),
                    });
                }
                return map;
            }, new Map<string, any>())
            .values()
    ), [allProducts, orderedLast60ByKey]);

    // Exclude products already spoken for — the Alternatives picker hides the
    // primary product and every already-added alternative (dedup by resolver key).
    const dedupedProducts = useMemo(() => {
        if (!excludeProductKeys || excludeProductKeys.size === 0) return uniqueProducts;
        return uniqueProducts.filter(product =>
            !excludeProductKeys.has(productKey(product.platform, product.articleId, product.name))
        );
    }, [uniqueProducts, excludeProductKeys]);

    // Filter products by activeTab (mainCategory: FOOD/DRINKS)
    const tabFilteredProducts = useMemo(() => {
        if (costPriceActiveTab === 'all') return dedupedProducts;

        const mainCategoryFilter = costPriceActiveTab.toUpperCase(); // 'food' -> 'FOOD', 'drinks' -> 'DRINKS'
        return dedupedProducts.filter(product =>
            product.mainCategory?.toUpperCase() === mainCategoryFilter
        );
    }, [dedupedProducts, costPriceActiveTab]);

    // Extract available suppliers and categories from tab-filtered products
    const availableSuppliers = useMemo(() => {
        const suppliers = new Set<string>();
        tabFilteredProducts.forEach(product => {
            if (product.platform) {
                suppliers.add(product.platform.toLowerCase());
            }
        });
        return Array.from(suppliers).sort();
    }, [tabFilteredProducts]);

    const availableCategories = useMemo(() => {
        const categories = new Set<string>();
        tabFilteredProducts.forEach(product => {
            if (product.category) {
                categories.add(product.category);
            }
        });
        return Array.from(categories).sort();
    }, [tabFilteredProducts]);

    // Apply supplier and category filters
    const filterAppliedProducts = useMemo(() => {
        let filtered = tabFilteredProducts;

        // Apply supplier filter
        if (supplierFilters.length > 0) {
            filtered = filtered.filter(product =>
                supplierFilters.includes(product.platform?.toLowerCase() || '')
            );
        }

        // Apply category filter
        if (categoryFilters.length > 0) {
            filtered = filtered.filter(product =>
                categoryFilters.includes(product.category || '')
            );
        }

        return filtered;
    }, [tabFilteredProducts, supplierFilters, categoryFilters]);

    // Filter products by relevance search (strict token match first, fuzzy only as a
    // bounded fallback — plain fuzzy returned most of the catalogue for short queries).
    // Use 'searchableId' instead of 'articleId' so platforms without articleId search correctly
    const productSearch = useProductSearch({
        data: filterAppliedProducts,
        keys: ['name', 'searchableId'],
        query: debouncedSearchQuery,
        limit: MAX_VISIBLE_RESULTS,
    });

    // Merge current restaurant (when selected) + selected siblings, tagging each
    // sibling item with its source restaurant for badging + persistence on add.
    const mergedCustomIngredients = useMemo(() => {
        const result: any[] = [];
        if (sourceRestaurantIds.includes(restaurantId)) {
            customIngredients.forEach(ci => result.push({ ...ci, sourceRestaurantId: undefined, sourceRestaurantName: undefined }));
        }
        sourceRestaurantIds
            .filter(id => id !== restaurantId)
            .forEach(id => {
                const data = sourceDataByRestaurant[id];
                if (!data) return;
                const name = restaurantNameById.get(id);
                data.customIngredients.forEach(ci => result.push({ ...ci, sourceRestaurantId: id, sourceRestaurantName: name }));
            });
        return result;
    }, [customIngredients, sourceRestaurantIds, restaurantId, sourceDataByRestaurant, restaurantNameById]);

    const mergedSubrecipes = useMemo(() => {
        const result: any[] = [];
        if (sourceRestaurantIds.includes(restaurantId)) {
            subrecipes.forEach(s => result.push({ ...s, sourceRestaurantId: undefined, sourceRestaurantName: undefined }));
        }
        sourceRestaurantIds
            .filter(id => id !== restaurantId)
            .forEach(id => {
                const data = sourceDataByRestaurant[id];
                if (!data) return;
                const name = restaurantNameById.get(id);
                data.subrecipes.forEach(s => result.push({ ...s, sourceRestaurantId: id, sourceRestaurantName: name }));
            });
        // Hide the edited subrecipe + its ancestors so a nested pick can't cycle.
        return excludeSubrecipeIds ? result.filter(s => !excludeSubrecipeIds.has(s.id)) : result;
    }, [subrecipes, sourceRestaurantIds, restaurantId, sourceDataByRestaurant, restaurantNameById, excludeSubrecipeIds]);

    // Filter custom ingredients by fuzzy search
    const filteredCustomIngredients = useFuzzySearch({
        data: mergedCustomIngredients,
        keys: ['productName', 'articleId'],
        query: debouncedSearchQuery,
        threshold: 0.5,
    });

    // Filter subrecipes by fuzzy search
    const filteredSubrecipes = useFuzzySearch({
        data: mergedSubrecipes,
        keys: ['name', 'description'],
        query: debouncedSearchQuery,
        threshold: 0.5,
    });

    // Filter cost price items by fuzzy search
    const filteredCostPriceItems = useFuzzySearch({
        data: availableCostPriceItems,
        keys: ['costPriceName', 'menuItemName'],
        query: debouncedSearchQuery,
        threshold: 0.5,
    });

    const visibleProducts = productSearch.results;
    const productsTruncatedCount = Math.max(0, productSearch.totalMatches - visibleProducts.length);

    // Helper to check if platform is a valid known platform
    const isKnownPlatform = (platformName: string): boolean => {
        const validPlatforms = Object.values(Platform).map(p => p.toString().toLowerCase());
        return validPlatforms.includes(platformName?.toLowerCase() || '');
    };

    // Helper to get platform display info for products
    const getProductPlatformDisplay = (platform: string, customPlatformName?: string): { showLogo: boolean; displayName: string; platformForLogo?: Platform } => {
        // First check if customPlatformName exists and matches a known platform
        if (customPlatformName && isKnownPlatform(customPlatformName)) {
            return {
                showLogo: true,
                displayName: customPlatformName,
                platformForLogo: customPlatformName.toLowerCase() as Platform
            };
        }

        // Then check if the platform field matches a known platform
        if (platform && isKnownPlatform(platform)) {
            return {
                showLogo: true,
                displayName: platform,
                platformForLogo: platform.toLowerCase() as Platform
            };
        }

        // Otherwise show as text: prefer customPlatformName, then platform, then "--"
        const displayName = customPlatformName || platform || '--';
        return {
            showLogo: false,
            displayName
        };
    };

    const handleTabChange = (tab: IngredientTab) => {
        setNewCustomName(null);
        setError(null);
        setActiveTab(tab);
        setReviewSearchOpen(true);
        setSearchQuery("");
        setSelectedProduct(null);
        setSelectedCustomIngredient(null);
        setSelectedSubrecipe(null);
        setSelectedCostPriceItem(null);
        setQuantityNeeded(1);
        setQuantityInputValue("1");
    };

    const handleSelectProduct = (product: any) => {
        if (lockProductSelection || (isAlternative && selectedProduct && productDedupKey(selectedProduct) === productDedupKey(product))) return;
        if (isReview) { setReviewSearchOpen(false); setSearchQuery(""); }
        setNewCustomName(null);
        setSelectedProduct(product);
        setSelectedCustomIngredient(null);
        setSelectedSubrecipe(null);
        setSelectedCostPriceItem(null);
        
        const quantity = defaultProductQuantity(product);
        setQuantityNeeded(quantity);
        setQuantityInputValue(String(quantity));
        if (isAlternative) setAlternativeWaste(String(parentWaste));
    };

    const handleSelectCustomIngredient = (ingredient: CustomIngredient) => {
        if (isReview) { setReviewSearchOpen(false); setSearchQuery(""); }
        setNewCustomName(null);
        setSelectedCustomIngredient(ingredient);
        setCustomUnit(ingredient.quantityInfo.stdUnit === "ml" ? "ml" : ingredient.quantityInfo.stdUnit === "g" ? "g" : "p");
        setSelectedProduct(null);
        setSelectedSubrecipe(null);
        setSelectedCostPriceItem(null);
        
        // Set default quantity based on unit type
        if (ingredient.quantityInfo?.stdUnit === Unit.GRAM || ingredient.quantityInfo?.stdUnit === Unit.MILLILITER) {
            setQuantityNeeded(100);
            setQuantityInputValue("100");
        } else {
            setQuantityNeeded(1);
            setQuantityInputValue("1");
        }
    };

    const handleSelectSubrecipe = (subrecipe: Subrecipe) => {
        if (isReview) { setReviewSearchOpen(false); setSearchQuery(""); }
        setNewCustomName(null);
        setSelectedSubrecipe(subrecipe);
        setSelectedProduct(null);
        setSelectedCustomIngredient(null);
        setSelectedCostPriceItem(null);
        
        // Set default quantity based on unit type
        const defaultQty = (subrecipe.portionsUnit === 'g' || subrecipe.portionsUnit === 'ml') ? 100 : 1;
        setQuantityNeeded(defaultQty);
        setQuantityInputValue(defaultQty.toString());
    };

    const handleSelectCostPriceItem = (item: any) => {
        setNewCustomName(null);
        setSelectedCostPriceItem(item);
        setSelectedProduct(null);
        setSelectedCustomIngredient(null);
        setSelectedSubrecipe(null);
        
        // Default to 1 portion
        setQuantityNeeded(1);
        setQuantityInputValue("1");
    };


    // Helper function to format custom ingredient unit price
    const formatCustomIngredientPrice = (ingredient: CustomIngredient) => {
        const base = showExVatOrders ? ingredient.price : (ingredient.priceWithTaxes ?? ingredient.price);
        const pricePerUnit = base / ingredient.quantityInfo.stdAmount;
        
        if (ingredient.quantityInfo.stdUnit === 'g') {
            return `${eur(pricePerUnit * 1000)}/kg`;
        } else if (ingredient.quantityInfo.stdUnit === 'ml') {
            return `${eur(pricePerUnit * 1000)}/l`;
        } else {
            return `${eur(pricePerUnit)}/${ingredient.quantityInfo.stdUnit}`;
        }
    };

    // Helper function to calculate total price of a subrecipe.
    // A sibling-sourced subrecipe is priced from that restaurant's orders + custom
    // ingredients (its own data set), matching how it will be costed once added.
    const calculateSubrecipePrice = (subrecipe: Subrecipe, portionsNeeded: number = subrecipe.portions) => {
        let totalPrice = 0;
        let hasValidPrices = false;

        const sourceId = (subrecipe as any).sourceRestaurantId as string | undefined;
        const sourceData = sourceId && sourceId !== restaurantId ? sourceDataByRestaurant[sourceId] : undefined;
        if (isReview) {
            if (!Number.isFinite(subrecipe.portions) || subrecipe.portions <= 0) return null;
            if (sourceId && sourceId !== restaurantId && !sourceData) return null;
            if (!sourceData && !practiceData && !dataOverride && (ordersLoading || orderRestaurantId !== restaurantId)) return null;
            const recipeMap = sourceData?.subrecipes ?? new Map(subrecipes.map(sub => [sub.id, sub]));
            const recipeOrders = sourceData?.orders ?? orders;
            const recipeCustoms = sourceData?.customIngredients ?? customIngredients;
            if (!hasSubrecipePrices(subrecipe, recipeOrders, { customIngredients: recipeCustoms, showExVat: showExVatOrders, allSubrecipes: recipeMap })) return null;
            const batchPrice = calculateSubrecipeTotalCost(subrecipe, recipeOrders, recipeCustoms, showExVatOrders, recipeMap, defaultWastePercentage);
            return Number.isFinite(batchPrice) ? batchPrice / subrecipe.portions * portionsNeeded : null;
        }
        const priceOrders = sourceData ? (sourceData.orders as typeof orders) : orders;
        const priceCustomIngredients = sourceData ? sourceData.customIngredients : customIngredients;

        subrecipe.ingredients.forEach(ingredient => {
            // Skip nested-subrecipe reference rows in this surface-level preview.
            if (ingredient.nestedSubrecipeId || ingredient.quantityNeeded === undefined) return;
            let ingredientPrice = 0;

            if (ingredient.isCustom && priceCustomIngredients.length > 0) {
                const customIngredient = priceCustomIngredients.find(ci =>
                    ci.articleId === ingredient.articleId && ci.platform === ingredient.platform
                );
                if (customIngredient) {
                    const base = showExVatOrders ? customIngredient.price : (customIngredient.priceWithTaxes ?? customIngredient.price);
                    const pricePerStdUnit = base / customIngredient.quantityInfo.stdAmount;
                    ingredientPrice = applyWaste(ingredient.quantityNeeded, resolveWaste(ingredient.wastePercentage, defaultWastePercentage)) * pricePerStdUnit;
                    hasValidPrices = true;
                }
            } else {
                // For platforms without articleId, match by product name
                const ingredientPlatformLower = ingredient.platform?.toLowerCase() || '';
                const useNameForMatching = platformsWithoutArticleId.includes(ingredientPlatformLower);

                const relevantOrders = priceOrders
                    .filter(order =>
                        order.platform === ingredient.platform &&
                        order.productData.some(product =>
                            useNameForMatching
                                ? product.name === ingredient.productName
                                : product.articleId === ingredient.articleId
                        )
                    )
                    .sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());

                if (relevantOrders.length > 0) {
                    const latestOrder = relevantOrders[0];
                    const product = latestOrder.productData.find(p =>
                        useNameForMatching
                            ? p.name === ingredient.productName
                            : p.articleId === ingredient.articleId
                    );
                    
                    if (product && product.quantityInfo) {
                        // Get the appropriate price based on VAT toggle
                        const productPrice = showExVatOrders ? product.price : (product.priceWithTaxes ?? product.price);
                        if (productPrice) {
                            const productQuantityInStdUnits = product.quantityInfo.stdAmount * product.quantityInfo.stdMultiplier;
                            const pricePerStdUnit = productPrice / productQuantityInStdUnits;
                            ingredientPrice = applyWaste(ingredient.quantityNeeded, resolveWaste(ingredient.wastePercentage, defaultWastePercentage)) * pricePerStdUnit;
                            hasValidPrices = true;
                        }
                    }
                }
            }

            totalPrice += ingredientPrice;
        });

        const portionRatio = portionsNeeded / subrecipe.portions;
        const scaledPrice = totalPrice * portionRatio;

        return hasValidPrices ? scaledPrice : null;
    };

    // Helper function to calculate estimated price for selected item and quantity
    const calculateEstimatedPrice = (): number | null => {
        if (quantityNeeded <= 0) return null;

        if (selectedProduct) {
            if (!selectedProduct.quantityInfo) return null;
            // Get the appropriate price based on VAT toggle
            const productPrice = showExVatOrders ? selectedProduct.price : (selectedProduct.priceWithTaxes ?? selectedProduct.price);
            if (!productPrice) return null;
            const unitPrice = calculateUnitPrice(productPrice, selectedProduct.quantityInfo, selectedProduct.category);
            if (!unitPrice) return null;
            // Calculate price per std unit
            const productQuantityInStdUnits = selectedProduct.quantityInfo.stdAmount * selectedProduct.quantityInfo.stdMultiplier;
            const pricePerStdUnit = productPrice / productQuantityInStdUnits;
            // New raw ingredient has no override yet → inherit the restaurant default
            // so the preview matches the row cost the instant it's added.
            return pricePerStdUnit * applyWaste(quantityNeeded, resolveWaste(isAlternative ? Number(alternativeWaste) : undefined, defaultWastePercentage));
        }

        if (selectedCustomIngredient) {
            const base = showExVatOrders
                ? selectedCustomIngredient.price
                : (selectedCustomIngredient.priceWithTaxes ?? selectedCustomIngredient.price);
            const pricePerStdUnit = base / selectedCustomIngredient.quantityInfo.stdAmount;
            return pricePerStdUnit * applyWaste(quantityNeeded, resolveWaste(undefined, defaultWastePercentage));
        }

        if (selectedSubrecipe) {
            return calculateSubrecipePrice(selectedSubrecipe, quantityNeeded);
        }

        if (selectedCostPriceItem) {
            // For nested cost price items, multiply total cost by quantity needed
            return selectedCostPriceItem.totalCost * quantityNeeded;
        }

        return null;
    };

    // Build the normalized selection handed to a pluggable persistence callback.
    const buildSelection = (): AddIngredientSelection | null => {
        if (selectedCostPriceItem) return { kind: "costPriceItem", costPriceItem: selectedCostPriceItem, quantityNeeded };
        if (selectedSubrecipe) return { kind: "subrecipe", subrecipe: selectedSubrecipe, quantityNeeded };
        if (selectedCustomIngredient) return { kind: "custom", customIngredient: selectedCustomIngredient, quantityNeeded };
        if (selectedProduct) return { kind: "product", product: selectedProduct, quantityNeeded, ...(isAlternative ? { wastePercentage: Number(alternativeWaste) } : {}) };
        return null;
    };

    const startNameOnlyCustom = (name: string) => {
        handleTabChange(activeTab);
        setReviewSearchOpen(false);
        setNewCustomName(name);
        setCustomUnit("g");
    };

    const handleAddIngredient = async () => {
        if (isLoading || (!selectedProduct && !selectedCustomIngredient && !selectedSubrecipe && !selectedCostPriceItem && !newCustomName?.trim()) ||
            !Number.isFinite(quantityNeeded) || quantityNeeded <= 0 || (isAlternative && !validAlternativeWaste)) return;

        // Pluggable persistence (subrecipe mode): hand the raw selection to the
        // caller and let it decide how to store it.
        if (onAddIngredient) {
            let selection = buildSelection();
            setIsLoading(true);
            setError(null);
            try {
                if (isReview && (newCustomName !== null || (selectedCustomIngredient?.price === 0 && selectedCustomIngredient.quantityInfo.stdUnit !== customUnit))) {
                    const name = newCustomName?.trim() || selectedCustomIngredient?.productName;
                    if (!name || practiceScope) return;
                    // A unit change creates/reuses the local name+unit identity. Never
                    // retain a sibling source tag on a newly local library reference.
                    const customIngredient = await ensureNameOnlyCustomIngredient(restaurantId, name, customUnit);
                    setCustomIngredients(current => [...current.filter(row => row.id !== customIngredient.id), customIngredient]);
                    selection = { kind: "custom", customIngredient, quantityNeeded };
                }
                if (!selection) return;
                await onAddIngredient(selection);
                if (returnAfterAdd) onBack();
            } catch (error) {
                console.error('Failed to add ingredient:', error);
                setError(error instanceof StaleRecipeWriteError ? error.message : isReview ? t("costPrice.automationRun.add.failed") : error instanceof Error ? error.message : t("costPrice.automationRun.add.failed"));
            } finally {
                setIsLoading(false);
            }
            return;
        }

        if (!analysisId) return;

        setIsLoading(true);
        setError(null);

        try {
            // Get current analysis
            const currentAnalysis = await fetchCostPriceAnalysisById(analysisId);

            let newIngredients: Omit<CostPriceIngredient, 'id' | 'currentPrice' | 'currentPriceDate'>[] = [];

            if (selectedCostPriceItem) {
                // Handle nested cost price item
                newIngredients = [{
                    articleId: `nested-${selectedCostPriceItem.id}`,
                    productName: selectedCostPriceItem.costPriceName,
                    quantityNeeded: 0,
                    quantityInfo: {
                        stdAmount: 1,
                        stdMultiplier: 1,
                        stdUnit: 'portion'
                    },
                    platform: 'nested',
                    nestedCostPriceId: selectedCostPriceItem.id,
                    nestedCostPriceName: selectedCostPriceItem.costPriceName,
                    nestedQuantityNeeded: quantityNeeded
                }];
            } else if (selectedSubrecipe) {
                // Add a single reference row — the cost calc resolves the subrecipe's
                // ingredients live from the subrecipes store at render time.
                // Sibling-sourced subrecipes carry sourceRestaurantId so they're
                // costed in that restaurant's context.
                newIngredients = [{
                    subrecipeId: selectedSubrecipe.id,
                    subrecipePortionsNeeded: quantityNeeded,
                    sourceRestaurantId: (selectedSubrecipe as any).sourceRestaurantId,
                    sourceRestaurantName: (selectedSubrecipe as any).sourceRestaurantName,
                }];
            } else if (selectedCustomIngredient) {
                // Handle custom ingredient
                newIngredients = [{
                    articleId: selectedCustomIngredient.articleId,
                    productName: selectedCustomIngredient.productName,
                    quantityNeeded: quantityNeeded,
                    quantityInfo: selectedCustomIngredient.quantityInfo,
                    platform: selectedCustomIngredient.platform,
                    customPlatformName: selectedCustomIngredient.customPlatformName,
                    custom: true,
                    sourceRestaurantId: (selectedCustomIngredient as any).sourceRestaurantId,
                    sourceRestaurantName: (selectedCustomIngredient as any).sourceRestaurantName,
                }];
            } else if (selectedProduct) {
                // Handle order history ingredient
                newIngredients = [{
                    articleId: selectedProduct.articleId,
                    productName: selectedProduct.name,
                    quantityNeeded: quantityNeeded,
                    quantityInfo: selectedProduct.quantityInfo || {
                        stdAmount: 1,
                        stdMultiplier: 1,
                        stdUnit: 'piece'
                    },
                    platform: selectedProduct.platform,
                    sourceRestaurantId: selectedProduct.sourceRestaurantId,
                    sourceRestaurantName: selectedProduct.sourceRestaurantName,
                }];
            } else {
                return;
            }
            
            // Update analysis with new ingredients
            await updateCostPriceAnalysis({
                id: analysisId,
                expectedUpdatedAt: recipeWriteVersion(currentAnalysis),
                ingredients: [...currentAnalysis.ingredients.map(toIngredientUpdatePayload), ...newIngredients]
            });
            
            onIngredientAdded?.();
            onBack();
        } catch (error) {
            console.error('Failed to add ingredient:', error);
            setError(error instanceof StaleRecipeWriteError ? error.message : isReview ? t("costPrice.automationRun.add.failed") : error instanceof Error ? error.message : t("costPrice.automationRun.add.failed"));
        } finally {
            setIsLoading(false);
        }
    };


    // Helper function to handle quantity changes
    const handleQuantityChange = (value: string, numericValue: number) => {
        setQuantityInputValue(value);
        setQuantityNeeded(numericValue);
    };

    /* ---------------------------------------------------------------------------
       Guided tour hooks. The picker owns its tab / search / selection locally, so a
       tour step can only ask it to move. Everything below is a no-op unless a tour
       is dispatching, and each listener ignores events aimed at the other panel:
       the create-subrecipe flow mounts a SECOND picker (mode="subrecipe") on top of
       this one, and both are listening at the same time.
       --------------------------------------------------------------------------- */
    // This picker belongs to the tour only when the drawer above it does. Outside that
    // scope no anchor is emitted and no `onboarding:cost-*` event is acted on, so a real
    // picker can never be driven into persisting something by a tour step.
    const isNestedPanel = mode === "subrecipe";
    const onboardingRef = useRef({
        activeTab, visibleProducts, filteredSubrecipes, mergedSubrecipes, handleTabChange,
        handleSelectProduct, handleSelectSubrecipe, handleQuantityChange, handleAddIngredient,
    });
    onboardingRef.current = {
        activeTab, visibleProducts, filteredSubrecipes, mergedSubrecipes, handleTabChange,
        handleSelectProduct, handleSelectSubrecipe, handleQuantityChange, handleAddIngredient,
    };

    useEffect(() => {
        if (!practiceScope) return;
        const forThisPanel = (event: Event) =>
            (((event as CustomEvent<{ panel?: string }>).detail?.panel === "subrecipe") === isNestedPanel);
        const byName = (list: any[], name: string, key: string) =>
            list.find(entry => (entry[key] ?? "").toLocaleLowerCase() === name.trim().toLocaleLowerCase());

        const onTab = (event: Event) => {
            if (!forThisPanel(event)) return;
            const tab = (event as CustomEvent<{ tab?: string }>).detail?.tab as IngredientTab | undefined;
            // Switching tabs clears search + selection, so never "switch" to the current one.
            if (tab && tab !== onboardingRef.current.activeTab) onboardingRef.current.handleTabChange(tab);
        };
        const onSearch = (event: Event) => {
            if (!forThisPanel(event)) return;
            setSearchQuery((event as CustomEvent<{ query?: string }>).detail?.query ?? "");
        };
        const onSelectProduct = (event: Event) => {
            if (!forThisPanel(event)) return;
            const { name, quantity } = (event as CustomEvent<{ name?: string; quantity?: string }>).detail ?? {};
            if (!name) return;
            const product = byName(onboardingRef.current.visibleProducts, name, "name");
            if (!product) return;
            onboardingRef.current.handleSelectProduct(product);
            if (quantity) onboardingRef.current.handleQuantityChange(quantity, parseFloat(quantity) || 0);
        };
        const onSelectSubrecipe = (event: Event) => {
            if (!forThisPanel(event)) return;
            const { id, name, quantity } = (event as CustomEvent<{ id?: string; name?: string; quantity?: string }>).detail ?? {};
            // Prefer the id: the sauce carries whatever name the user typed, and an active
            // search could have filtered it out of the visible list.
            const subrecipe = id
                ? onboardingRef.current.mergedSubrecipes.find((entry: any) => entry.id === id)
                : name ? byName(onboardingRef.current.filteredSubrecipes, name, "name") : undefined;
            if (!subrecipe) return;
            onboardingRef.current.handleSelectSubrecipe(subrecipe);
            if (quantity) onboardingRef.current.handleQuantityChange(quantity, parseFloat(quantity) || 0);
        };
        const onConfirm = (event: Event) => {
            if (!forThisPanel(event)) return;
            void onboardingRef.current.handleAddIngredient();
        };
        const onOpenCreate = () => { if (!isNestedPanel) setShowCreateSubrecipeModal(true); };
        const onCloseCreate = () => { if (!isNestedPanel) setShowCreateSubrecipeModal(false); };

        window.addEventListener("onboarding:cost-set-add-tab", onTab);
        window.addEventListener("onboarding:cost-set-search", onSearch);
        window.addEventListener("onboarding:cost-select-product", onSelectProduct);
        window.addEventListener("onboarding:cost-select-subrecipe", onSelectSubrecipe);
        window.addEventListener("onboarding:cost-confirm-add", onConfirm);
        window.addEventListener("onboarding:cost-open-create-subrecipe", onOpenCreate);
        window.addEventListener("onboarding:cost-close-create-subrecipe", onCloseCreate);
        return () => {
            window.removeEventListener("onboarding:cost-set-add-tab", onTab);
            window.removeEventListener("onboarding:cost-set-search", onSearch);
            window.removeEventListener("onboarding:cost-select-product", onSelectProduct);
            window.removeEventListener("onboarding:cost-select-subrecipe", onSelectSubrecipe);
            window.removeEventListener("onboarding:cost-confirm-add", onConfirm);
            window.removeEventListener("onboarding:cost-open-create-subrecipe", onOpenCreate);
            window.removeEventListener("onboarding:cost-close-create-subrecipe", onCloseCreate);
        };
    }, [isNestedPanel, practiceScope]);

    // Render items based on active tab
    const renderTabContent = () => {
        if (activeTab === "From Orders") {
            return (
                <FromOrdersTab
                    hideFilters={isReview && !reviewFiltersOpen}
                    products={visibleProducts}
                    truncatedCount={productsTruncatedCount}
                    selectedProduct={selectedProduct}
                    onSelectProduct={handleSelectProduct}
                    quantityInputValue={quantityInputValue}
                    quantityNeeded={quantityNeeded}
                    onQuantityChange={handleQuantityChange}
                    calculateEstimatedPrice={calculateEstimatedPrice}
                    getProductPlatformDisplay={getProductPlatformDisplay}
                    searchQuery={searchQuery}
                    showExVatOrders={showExVatOrders}
                    // Filter props
                    supplierFilters={supplierFilters}
                    onSupplierFiltersChange={setSupplierFilters}
                    availableSuppliers={availableSuppliers}
                    categoryFilters={categoryFilters}
                    onCategoryFiltersChange={setCategoryFilters}
                    availableCategories={availableCategories}
                    currentRestaurantId={restaurantId}
                    hideSourceFilter={restrictedSetupSources || !!dataOverride || (isReview && !reviewFiltersOpen)}
                    automationReview={isReview || isAlternative}
                    onboardingScope={practiceScope && isNestedPanel ? "subrecipe" : undefined}
                    onCreateCustom={isReview && !practiceScope ? startNameOnlyCustom : undefined}
                />
            );
        } else if (activeTab === "Custom Ingredients" && !practiceScope) {
            return (
                <CustomIngredientsTab
                    ingredients={filteredCustomIngredients}
                    selectedIngredient={selectedCustomIngredient}
                    onSelectIngredient={handleSelectCustomIngredient}
                    quantityInputValue={quantityInputValue}
                    onQuantityChange={handleQuantityChange}
                    calculateEstimatedPrice={calculateEstimatedPrice}
                    getProductPlatformDisplay={getProductPlatformDisplay}
                    formatCustomIngredientPrice={formatCustomIngredientPrice}
                    onCreateClick={() => { if (!practiceScope) isReview ? startNameOnlyCustom(searchQuery.trim()) : setShowCreateModal(true); }}
                    searchQuery={searchQuery}
                    currentRestaurantId={restaurantId}
                    hideSourceFilter={restrictedSetupSources || !!dataOverride || (isReview && !reviewFiltersOpen)}
                    automationReview={isReview}
                />
            );
        } else if (activeTab === "Sub recipes") {
            return (
                <SubrecipesTab
                    subrecipes={filteredSubrecipes}
                    selectedSubrecipe={selectedSubrecipe}
                    onSelectSubrecipe={handleSelectSubrecipe}
                    quantityInputValue={quantityInputValue}
                    onQuantityChange={handleQuantityChange}
                    calculateEstimatedPrice={calculateEstimatedPrice}
                    calculateSubrecipePrice={calculateSubrecipePrice}
                    onCreateClick={() => setShowCreateSubrecipeModal(true)}
                    searchQuery={searchQuery}
                    currentRestaurantId={restaurantId}
                    hideSourceFilter={restrictedSetupSources || !!dataOverride || (isReview && !reviewFiltersOpen)}
                    automationReview={isReview}
                    allowCreate={mode !== "subrecipe"}
                />
            );
        } else {
            // Menu Items
            return (
                <MenuItemsTab
                    items={filteredCostPriceItems}
                    selectedItem={selectedCostPriceItem}
                    onSelectItem={handleSelectCostPriceItem}
                    quantityInputValue={quantityInputValue}
                    onQuantityChange={handleQuantityChange}
                    calculateEstimatedPrice={calculateEstimatedPrice}
                    searchQuery={searchQuery}
                />
            );
        }
    };

    return (
        <>
            <div className={`flex flex-col overflow-hidden ${inline ? "rounded-2xl bg-backgroundSecondary dark:bg-backgroundSecondary-dark" : "h-full"}`} data-onboarding={scopedAnchor(practiceScope, isNestedPanel ? "cost-sub-add-panel" : "cost-add-panel")}>
                {/* Header */}
                {!inline && <div className={`hidden md:flex items-center justify-between px-5 py-4 border-b bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                    <button
                        type="button"
                        onClick={onBack} disabled={isLoading}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-borderGrey dark:border-borderGrey-dark"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.8337 10H4.16699" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10.0003 15.8333L4.16699 9.99996L10.0003 4.16663" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={onClose} disabled={isLoading}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-borderGrey dark:border-borderGrey-dark"
                    >
                        <img src="/assets/close-modal.svg" alt="close" />
                    </button>
                </div>}

                {/* Content */}
                <div className={`flex-1 flex flex-col min-h-0 overflow-hidden ${inline ? "p-4 lg:p-6" : "px-4 sm:px-5 py-5"}`}>
                    {!inline && <div className="flex items-center gap-4 mb-5">
                        <button
                            type="button"
                            onClick={onBack} disabled={isLoading}
                            className="block md:hidden"
                        >
                            <img src="/assets/arrow-narrow-left.svg" alt="arrow-narrow-left" />
                        </button>
                        <h2 className={`text-[24px] sm:text-[28px] leading-9 sm:leading-[39px] ${primaryText} font-bold`}>
                            {title ?? t("costPrice.createSubrecipe.addIngredients")}
                        </h2>
                    </div>}
                    {inline && <div className="mb-4 flex items-center justify-between gap-3">
                        <h3 className={`text-base font-medium ${primaryText}`}>{t("costPrice.automationRun.add.title")}</h3>
                        <button type="button" onClick={onClose} disabled={isLoading} aria-label={t("costPrice.automationRun.add.close")} className={`p-2 ${secondaryText}`}><X size={16} /></button>
                    </div>}

                    {isAlternative && alternativePrimary && (
                        <div className={`mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border p-3 text-sm ${borderPrimary} bg-background dark:bg-background-dark`}>
                            <span className={`min-w-0 break-words font-semibold ${primaryText}`}>{alternativePrimary.productName}</span>
                            <span className={secondaryText}>{alternativePrimary.quantityNeeded} {alternativePrimary.quantityInfo?.stdUnit}</span>
                            <span className={secondaryText}>{t("costPrice.alternatives.wasteLabel")}: {parentWaste}%</span>
                        </div>
                    )}

                    {error && (
                        <div className={`mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-[14px]`}>
                            {error}
                        </div>
                    )}

                    <div className={`flex flex-1 flex-col min-h-0 gap-4 ${inline ? "" : "pt-5 sm:pt-6"}`}>
                        {/* Tabs — hidden when only a single tab is available (e.g. alternatives mode). */}
                        {availableTabs.length > 1 && (
                            <div className={isReview ? `flex w-full lg:w-auto self-start max-w-full items-center overflow-x-auto rounded-lg border bg-background dark:bg-background-dark ${borderPrimary}` : "flex items-center justify-between rounded-md bg-[#F4F5F5] overflow-auto dark:bg-backgroundDarker-dark"}>
                                {availableTabs.map((tab) => {
                                    const isActive = tab === activeTab;
                                    const activeClasses = isReview
                                        ? "bg-cardLightGreen dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark"
                                        : `bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${primaryText} border border-borderGrey dark:border-borderGrey-dark`;
                                    return (
                                        <button
                                            key={tab}
                                            type="button"
                                            data-onboarding={!practiceScope ? undefined
                                                : tab === "From Orders"
                                                    ? (isNestedPanel ? "cost-sub-add-from-orders" : "cost-add-from-orders")
                                                    : tab === "Sub recipes" && !isNestedPanel
                                                        ? "cost-add-subrecipes-tab"
                                                        : undefined
                                            }
                                            onClick={() => handleTabChange(tab)}
                                            className={`${isReview ? `flex-1 lg:flex-none px-2 lg:px-3 min-h-[44px] lg:min-h-9 text-[11px] lg:text-xs border-r last:border-r-0 ${borderPrimary}` : "px-5 h-10 text-[14px] rounded-lg"} leading-[18px] font-medium whitespace-nowrap transition-colors ${
                                                isActive ? activeClasses : greyTextColor
                                            }`}
                                        >
                                            {isReview ? t(`costPrice.automationRun.add.${tab === "From Orders" ? "orderProducts" : tab === "Sub recipes" ? "subrecipes" : "customIngredient"}`) : tab}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Search */}
                        {!lockProductSelection && <div className="relative" data-onboarding={scopedAnchor(practiceScope, isNestedPanel ? "cost-sub-add-search" : "cost-add-search")}>
                            <span className={`pointer-events-none absolute inset-y-0 left-3 flex items-center ${secondaryText}`}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="9.21601" cy="9.21552" r="5.88495" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16.67 16.6695L13.377 13.3765" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <input
                                type="text"
                                placeholder={t(isReview && activeTab === "Sub recipes" ? "costPrice.automationRun.add.searchSubrecipes" : isReview && activeTab === "Custom Ingredients" ? "costPrice.automationRun.add.searchCustom" : "costPrice.automationRun.add.search")}
                                value={searchQuery}
                                onFocus={() => { if (isReview) setReviewSearchOpen(true); }}
                                onKeyDown={event => { if (isReview && event.key === "Escape") setReviewSearchOpen(false); }}
                                onChange={(e) => { setSearchQuery(e.target.value); setReviewSearchOpen(true); }}
                                className={`w-full rounded-lg border ${isReview ? "bg-background dark:bg-background-dark pr-12 h-11" : "bg-backgroundSecondary dark:bg-backgroundSecondary-dark pr-3 h-10"} pl-9 text-[14px] leading-5 ${secondaryText} ${placeholderPrimary} outline-none border-borderGrey dark:border-borderGrey-dark`}
                            />
                            {isReview && <button type="button" aria-label={t("costPrice.automationRun.add.filters")} aria-expanded={reviewFiltersOpen}
                                onClick={() => { setReviewFiltersOpen(value => !value); setReviewSearchOpen(true); }}
                                className={`absolute right-1 top-0 flex h-11 w-11 items-center justify-center ${reviewFiltersOpen ? "text-darkGreenColor dark:text-darkGreenColor-dark" : secondaryText}`}><SlidersHorizontal size={16} /></button>}
                        </div>}

                        {/* Tab Content */}
                        {!lockProductSelection && (!isReview || reviewSearchOpen) && (inline
                            ? <div className={`-mt-3 flex max-h-64 min-h-0 flex-col overflow-y-auto rounded-lg border bg-background dark:bg-background-dark shadow-md ${borderPrimary}`}>{renderTabContent()}</div>
                            : renderTabContent())}
                        {isAlternative && selectedProduct && (
                            <div className={`shrink-0 rounded-lg border p-3 ${borderPrimary} bg-background dark:bg-background-dark`}>
                                <div className="mb-3 flex items-center gap-3">
                                    <SupplierLogo platform={selectedProduct.platform} size="small" />
                                    <span className={`min-w-0 break-words text-sm font-semibold ${primaryText}`}>{selectedProduct.name}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className={`min-w-0 text-sm ${secondaryText}`}>
                                        {t("costPrice.alternatives.quantityLabel")}
                                        <div className={`mt-1 flex h-11 items-center rounded-lg border px-3 ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark`}>
                                            <input type="number" inputMode="decimal" min={0} step="any" value={quantityInputValue}
                                                onChange={event => handleQuantityChange(event.target.value, Number(event.target.value))}
                                                className={`min-w-0 w-full bg-transparent dark:bg-transparent outline-none ${primaryText}`} />
                                            <span className="ml-2 shrink-0">{selectedProduct.quantityInfo?.stdUnit}</span>
                                        </div>
                                    </label>
                                    <label className={`min-w-0 text-sm ${secondaryText}`}>
                                        {t("costPrice.alternatives.wasteLabel")}
                                        <div className={`mt-1 flex h-11 items-center rounded-lg border px-3 ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark`}>
                                            <input type="number" inputMode="decimal" min={0} max={100} step="any" value={alternativeWaste}
                                                onChange={event => setAlternativeWaste(event.target.value)}
                                                className={`min-w-0 w-full bg-transparent dark:bg-transparent outline-none ${primaryText}`} />
                                            <span className="ml-2">%</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        )}
                        {isReview && (
                            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[#F4F4F4] dark:bg-backgroundDarker-dark p-2">
                                <div className="min-w-0 flex-1 basis-full sm:basis-40">
                                    <span className={`block text-[10px] uppercase ${greyTextColor}`}>{t(activeTab === "Sub recipes" ? "costPrice.automationRun.add.subrecipe" : activeTab === "Custom Ingredients" ? "costPrice.automationRun.add.customIngredient" : "costPrice.automationRun.add.product")}</span>
                                    {newCustomName !== null ? <input
                                        aria-label={t("costPrice.automationRun.add.name")}
                                        value={newCustomName}
                                        onChange={event => { setNewCustomName(event.target.value); }}
                                        placeholder={t("costPrice.automationRun.add.name")}
                                        className={`w-full bg-transparent dark:bg-transparent py-1 text-sm outline-none ${primaryText} ${placeholderPrimary}`}
                                    /> : <span className={`block truncate text-sm ${primaryText}`}>{selectedProduct?.name ?? selectedCustomIngredient?.productName ?? selectedSubrecipe?.name ?? "--"}</span>}
                                </div>
                                <div className={`flex h-10 items-center overflow-hidden rounded-lg border bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                                    <input type="number" inputMode="decimal" min={0} step="any" value={newCustomName !== null || selectedProduct || selectedCustomIngredient || selectedSubrecipe ? quantityInputValue : ""} placeholder="--" disabled={newCustomName === null && !selectedProduct && !selectedCustomIngredient && !selectedSubrecipe}
                                        aria-label={t("costPrice.automationRun.add.amount")}
                                        onChange={event => handleQuantityChange(event.target.value, Number(event.target.value))}
                                        className={`w-20 bg-transparent dark:bg-transparent px-3 outline-none ${primaryText}`} />
                                    {newCustomName !== null || selectedCustomIngredient?.price === 0 || (!selectedProduct && !selectedCustomIngredient && !selectedSubrecipe) ? (
                                        <select aria-label={t("costPrice.automationRun.add.unit")} value={customUnit}
                                            onChange={event => setCustomUnit(event.target.value as "g" | "ml" | "p")}
                                            className={`h-full border-l bg-background dark:bg-background-dark px-2 ${borderPrimary} ${primaryText}`}>
                                            {["g", "ml", "p"].map(unit => <option key={unit} value={unit}>{unit === "p" ? t("costPrice.addIngredient.piece") : unit}</option>)}
                                        </select>
                                    ) : <span className={`flex h-full min-w-16 items-center justify-center border-l bg-[#F4F4F4] dark:bg-backgroundDarker-dark px-3 ${borderPrimary} ${secondaryText}`}>{(selectedProduct?.quantityInfo?.stdUnit ?? selectedCustomIngredient?.quantityInfo.stdUnit ?? selectedSubrecipe?.portionsUnit) === "p" ? t("costPrice.addIngredient.piece") : selectedProduct?.quantityInfo?.stdUnit ?? selectedCustomIngredient?.quantityInfo.stdUnit ?? selectedSubrecipe?.portionsUnit}</span>}
                                </div>
                                <button type="button" onClick={handleAddIngredient} aria-label={t("costPrice.detail.addIngredient")}
                                    disabled={isLoading || (newCustomName === null && !selectedProduct && !selectedCustomIngredient && !selectedSubrecipe) || !Number.isFinite(quantityNeeded) || quantityNeeded <= 0 || (newCustomName !== null && !newCustomName.trim())}
                                    className={`h-10 w-10 shrink-0 justify-center ${greenColorClass} disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed`}><Plus size={20} /></button>
                                <button type="button" onClick={() => handleTabChange(activeTab)} aria-label={t("costPrice.automationRun.add.clearSelection")} className={`p-2 ${secondaryText}`}><X size={16} /></button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action buttons — pinned footer so they stay in view regardless of list/filter height */}
                {!isReview && <div className={`flex items-center justify-between gap-4 sm:gap-6 px-4 sm:px-5 py-4 border-t bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                    <button
                        type="button"
                        onClick={onBack} disabled={isLoading}
                        className={`flex-1 h-11 justify-center text-[14px] leading-5 font-semibold ${cancelColorClass}`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-onboarding={scopedAnchor(practiceScope, isNestedPanel ? "cost-sub-add-confirm" : "cost-add-confirm")}
                        onClick={handleAddIngredient}
                        disabled={(!selectedProduct && !selectedCustomIngredient && !selectedSubrecipe && !selectedCostPriceItem) || !Number.isFinite(quantityNeeded) || quantityNeeded <= 0 || (isAlternative && !validAlternativeWaste) || isLoading}
                        className={`flex-1 h-11 justify-center text-[16px] leading-6 font-semibold gap-2 ${greenColorClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {isLoading ? (
                            "Adding..."
                        ) : (
                            <>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 4.16663V15.8333" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M4.16699 10H15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {addLabel ?? t("costPrice.detail.addIngredient")}
                            </>
                        )}
                    </button>
                </div>}
            </div>

            {/* Create Custom Ingredient Modal (new side modal, create mode).
                Both this and the subrecipe panel below portal to body, so they need an
                explicit top layer: this picker is always reached from inside another
                panel, and some of those hosts sit above the portal's default z-30. */}
            {showCreateModal && !practiceScope && (
                <CustomIngredientDetailModal
                    zIndexClass={overlayTopLayer}
                    ingredient={null}
                    restaurantId={restaurantId}
                    onClose={() => setShowCreateModal(false)}
                    onSaved={async () => {
                        try {
                            const ingredients = await fetchCustomIngredientsByRestaurant(restaurantId);
                            setCustomIngredients(ingredients);
                        } catch (error) {
                            console.error('Failed to reload custom ingredients:', error);
                        }
                    }}
                />
            )}

            {/* Create Subrecipe Modal (new side modal, create mode) */}
            {showCreateSubrecipeModal && (
                <Suspense fallback={null}>
                    <SubrecipeDetailModal
                        zIndexClass={overlayTopLayer}
                        subrecipeId={null}
                        restaurantId={restaurantId}
                        allowEmptyIngredients={isReview}
                        seedName={isReview ? searchQuery.trim() : undefined}
                        onClose={() => setShowCreateSubrecipeModal(false)}
                        onSaved={async (saved) => {
                            try {
                                const subrecipeList = practiceScope ? getPracticeDataset()?.subrecipesList ?? [] : await fetchSubrecipesByRestaurant(restaurantId);
                                setSubrecipes(subrecipeList);
                                if (isReview && saved) handleSelectSubrecipe(saved);
                            } catch (error) {
                                console.error('Failed to reload subrecipes:', error);
                            }
                        }}
                    />
                </Suspense>
            )}
        </>
    );
};

export default AddIngredients;
